/**
 * YT Sub-Navigation – Custom Element
 * Sticky applied via JS to the section wrapper (like Dawn's StickyHeader).
 * Active-state tracking via IntersectionObserver, smooth scroll.
 * Reveal-on-scroll via scroll listener with clip-path animation.
 */
if (!customElements.get('yt-sub-nav')) {
  class YTSubNav extends HTMLElement {
    constructor() {
      super();
      this.links = [];
      this.sections = [];
      this.observer = null;
      this.sectionWrapper = null;
      this.config = {};
      this.isRevealed = false;
      this.onScrollReveal = null;
    }

    connectedCallback() {
      this.links = [...this.querySelectorAll('.yt-sub-nav__link')];
      if (!this.links.length) return;

      this.sectionWrapper = this.closest('.section-yt-sub-nav');
      this.listWrap = this.querySelector('.yt-sub-nav__list-wrap');
      this.navList = this.querySelector('.yt-sub-nav__list');
      this.loadConfig();
      this.collectSections();
      this.bindClicks();
      this.initActiveTracking();
      this.initFadeEdges();

      if (this.dataset.sticky === 'true' && this.sectionWrapper) {
        this.initSticky();
      }

      if (this.dataset.reveal === 'true' && this.sectionWrapper) {
        this.initReveal();
      }
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      if (this.onScrollReveal) {
        window.removeEventListener('scroll', this.onScrollReveal);
      }
      if (this.updateFadeEdges) {
        window.removeEventListener('resize', this.updateFadeEdges);
      }
    }

    loadConfig() {
      const configEl = this.parentElement.querySelector('[data-yt-sub-nav-config]');
      if (configEl) {
        try {
          this.config = JSON.parse(configEl.textContent);
        } catch (e) {
          this.config = {};
        }
      }
    }

    /**
     * Resolve an element to a "real" section that has measurable height.
     * If the target is a placeholder (.yt-anchor) inside an empty
     * .shopify-section wrapper, walk to the next sibling section instead.
     */
    resolveSection(el) {
      if (!el) return null;
      if (el.classList.contains('yt-anchor')) {
        const wrapper = el.closest('.shopify-section');
        if (wrapper) {
          const next = wrapper.nextElementSibling;
          if (next && next.classList.contains('shopify-section')) return next;
        }
      }
      return el.closest('.shopify-section') || el;
    }

    collectSections() {
      this.sections = this.links
        .map((link) => {
          const id = link.getAttribute('href')?.replace('#', '');
          const raw = id ? document.getElementById(id) : null;
          const resolved = this.resolveSection(raw);
          if (resolved) resolved.dataset.ytSubNavTargetId = id;
          return resolved;
        })
        .filter(Boolean);
    }

    bindClicks() {
      this.links.forEach((link) => {
        link.addEventListener('click', (e) => {
          const id = link.getAttribute('href')?.replace('#', '');
          const raw = id ? document.getElementById(id) : null;
          const target = this.resolveSection(raw);
          if (!target) return;

          e.preventDefault();
          const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '0', 10);
          const subNavHeight = this.sectionWrapper ? this.sectionWrapper.offsetHeight : 0;
          const top = target.getBoundingClientRect().top + window.scrollY - headerHeight - subNavHeight;
          window.scrollTo({ top, behavior: 'smooth' });
          history.replaceState(null, '', `#${id}`);
        });
      });
    }

    /**
     * Sticky — apply position:sticky to the Shopify section wrapper.
     * Same pattern as Dawn's StickyHeader.
     */
    initSticky() {
      this.sectionWrapper.style.position = 'sticky';
      this.sectionWrapper.style.top = 'var(--header-height, 0px)';
      this.sectionWrapper.style.zIndex = '2';

      if (this.config.backgroundColor) {
        this.sectionWrapper.style.backgroundColor = this.config.backgroundColor;
      }
    }

    /**
     * Reveal on scroll — section hidden via display:none initially,
     * shown with clip-path slide-in animation when user scrolls past.
     */
    initReveal() {
      this.revealThreshold = this.sectionWrapper.offsetTop;
      this.sectionWrapper.style.display = 'none';

      this.onScrollReveal = () => {
        const scrollY = window.scrollY || window.pageYOffset;

        if (scrollY >= this.revealThreshold && !this.isRevealed) {
          this.isRevealed = true;
          this.sectionWrapper.style.display = '';
          this.sectionWrapper.style.animation = 'ytSubNavSlideIn 0.35s ease forwards';
        } else if (scrollY < this.revealThreshold && this.isRevealed) {
          this.isRevealed = false;
          this.sectionWrapper.style.display = 'none';
          this.sectionWrapper.style.animation = '';
        }
      };

      window.addEventListener('scroll', this.onScrollReveal, { passive: true });
      this.onScrollReveal();
    }

    initActiveTracking() {
      if (!this.sections.length) return;

      const activeColor = this.config.activeColor || '';
      const textColor = this.config.textColor || '';
      const textOpacity = this.config.textOpacity ?? 1;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const id = entry.target.dataset.ytSubNavTargetId || entry.target.id;
            const link = this.links.find(
              (l) => l.getAttribute('href') === `#${id}`
            );
            if (!link || link.classList.contains('yt-sub-nav__link--cta')) return;

            if (entry.isIntersecting) {
              // Reset all non-CTA links to original color
              this.links.forEach((l) => {
                if (!l.classList.contains('yt-sub-nav__link--cta')) {
                  l.classList.remove('is-active');
                  l.removeAttribute('aria-current');
                  if (textColor) l.style.color = textColor;
                  l.style.opacity = String(textOpacity);
                }
              });
              // Set active link
              link.classList.add('is-active');
              link.setAttribute('aria-current', 'true');
              if (activeColor) link.style.color = activeColor;
              link.style.opacity = '1';
              this.scrollActiveIntoView(link);
            }
          });
        },
        { rootMargin: '-20% 0px -70% 0px' }
      );

      this.sections.forEach((section) => this.observer.observe(section));
    }

    /**
     * Fade-edges — show gradient hints when nav list is scrollable.
     */
    initFadeEdges() {
      if (!this.listWrap || !this.navList) return;

      this.updateFadeEdges = () => {
        const el = this.navList;
        const scrollLeft = Math.round(el.scrollLeft);
        const maxScroll = el.scrollWidth - el.clientWidth;

        this.listWrap.classList.toggle('can-scroll-start', scrollLeft > 2);
        this.listWrap.classList.toggle('can-scroll-end', scrollLeft < maxScroll - 2);
      };

      this.navList.addEventListener('scroll', this.updateFadeEdges, { passive: true });
      window.addEventListener('resize', this.updateFadeEdges, { passive: true });

      // Initial check
      this.updateFadeEdges();
    }

    /**
     * Auto-scroll the active link into the visible area of the nav.
     */
    scrollActiveIntoView(link) {
      if (!this.navList || !link) return;
      link.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }
  }

  customElements.define('yt-sub-nav', YTSubNav);
}
